import styles from '../index.module.css'

import { FaRegSadTear } from '@react-icons/all-files/fa/FaRegSadTear'
import React from 'react'

import { Flex, Table, Text } from 'components/ds'
import NestedTracksPanel from 'components/search/nested-tracks-panel'
import Skeleton from 'components/ui/skeleton'
import {
  SearchNestingProvider,
  useSearchNesting,
} from 'contexts/search-nesting'

import { TableRow } from './table-row'

export const TableBody: React.FC<{
  data: Record<string, any>[]
  loading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  table
  rowActionsOpen
  isPartOfMultipleSelection
  handleTrackClick
  selectedRowItems
  columns
  sentinelRef
  skeletonRef
  getPinningStyles
  location
  searchType
  searchResultsSource
}> = ({
  data,
  loading,
  isFetchingNextPage,
  hasNextPage,
  table,
  rowActionsOpen,
  isPartOfMultipleSelection,
  handleTrackClick,
  selectedRowItems,
  columns,
  sentinelRef,
  skeletonRef,
  getPinningStyles,
  location,
  searchType,
  searchResultsSource,
}) => {
  return (
    <Table.Body>
      {/* Show skeleton rows when loading new search results */}
      {loading &&
        !isFetchingNextPage &&
        // Generate 12 skeleton rows for initial loading
        Array(12)
          .fill(0)
          .map((_, index) => (
            <Table.Row
              key={`skeleton-${index}`}
              ref={index === 0 ? skeletonRef : undefined}
              className={styles.SkeletonRow}
              style={{
                animationDelay: `${index * 0.02}s`,
              }}
            >
              {table.getVisibleLeafColumns().map((column, colIndex) => (
                <Table.Cell
                  key={colIndex}
                  className={styles.TableCell}
                  style={{
                    minWidth: column.getSize(),
                    height: '56px', // Fixed height for skeleton rows
                    padding: '16px 8px', // Consistent padding
                  }}
                >
                  <Skeleton type="block" lines={1} />
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
      {!data?.length && !loading && !isFetchingNextPage ? (
        <Table.Row>
          <Table.Cell colSpan={columns.length} align="center">
            <Flex p="9" gap="1" align="center" justify="center">
              <FaRegSadTear />
              <Text weight="bold" color="gray">
                No results found.
              </Text>
            </Flex>
          </Table.Cell>
        </Table.Row>
      ) : null}
      {/* Render data rows only when not loading new search results */}
      {data?.length > 0 && !loading && (
        <SearchNestingProvider>
          <NestedRowsRenderer
            table={table}
            rowActionsOpen={rowActionsOpen}
            isPartOfMultipleSelection={isPartOfMultipleSelection}
            handleTrackClick={handleTrackClick}
            selectedRowItems={selectedRowItems}
            getPinningStyles={getPinningStyles}
            location={location}
            searchType={searchType}
            searchResultsSource={searchResultsSource}
          />
          {/* Skeleton rows for infinite scroll - first row is sentinel */}
          {hasNextPage &&
            Array(5)
              .fill(0)
              .map((_, index) => (
                <Table.Row
                  key={`infinite-skeleton-${index}`}
                  ref={index === 0 ? sentinelRef : undefined}
                  className={styles.SkeletonRow}
                  style={{
                    animationDelay: `${index * 0.02}s`,
                  }}
                >
                  {table.getVisibleLeafColumns().map((column, colIndex) => (
                    <Table.Cell
                      key={colIndex}
                      className={styles.TableCell}
                      style={{
                        minWidth: column.getSize(),
                        height: '56px',
                        padding: '16px 8px',
                      }}
                    >
                      <Skeleton type="block" lines={1} />
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
        </SearchNestingProvider>
      )}
    </Table.Body>
  )
}

type NestedRowsRendererProps = {
  table: any
  rowActionsOpen: { id: string; menu: string } | null
  isPartOfMultipleSelection: (_row: any) => boolean
  handleTrackClick: (_event: any, _row: any) => void
  selectedRowItems: any[]
  getPinningStyles: (_column: any) => React.CSSProperties
  location: string
  searchType?: string
  searchResultsSource?: string
}

const NestedRowsRenderer = ({
  table,
  rowActionsOpen,
  isPartOfMultipleSelection,
  handleTrackClick,
  selectedRowItems,
  getPinningStyles,
  location,
  searchType,
  searchResultsSource,
}: NestedRowsRendererProps) => {
  const { isExpanded: isTrackExpanded } = useSearchNesting()
  const visibleColumns = table.getVisibleLeafColumns()
  const colSpan = visibleColumns.length

  return (
    <>
      {table.getRowModel().rows.map(row => {
        const multipleSelected = isPartOfMultipleSelection(row)
        const isExpanded = isTrackExpanded(row.original.id)
        return (
          <React.Fragment key={row.id}>
            <TableRow
              row={row}
              rowActionsOpen={rowActionsOpen}
              rowIsSelected={row.getIsSelected()}
              visibleColumns={visibleColumns}
              isPartOfMultipleSelection={multipleSelected}
              isExpanded={isExpanded}
              handleTrackClick={handleTrackClick}
              selectedRowItems={selectedRowItems}
              getPinningStyles={getPinningStyles}
              location={location}
              searchType={searchType}
              searchResultsSource={searchResultsSource}
            />
            {isExpanded && (
              <tr>
                <NestedTracksPanel
                  track={row.original}
                  colSpan={colSpan}
                  visibleColumns={visibleColumns}
                />
              </tr>
            )}
          </React.Fragment>
        )
      })}
    </>
  )
}
